/**
=========================================================
* Trust Cargo - opening one record from somewhere else
=========================================================

* A notice is about one order or one container, and clicking it should land
* on that record - its data in front of you, editable - not on a list to go
* looking through. The orders and containers screens already have that: the
* edit form, which loads a record by its id whether or not the list behind it
* is showing the row.
*
* So a notice leads to the screen with the record named in the address -
* /orders?open=512 - and the screen opens its edit form on it. The address is
* the hand-over rather than navigation state because it can be read, sent to
* somebody and opened in a new tab, and means the same thing every time.
*/

import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "open";

/**
 * Where a notice leads.
 *
 * An order notice carries an order_id, and that wins: the delivery-note
 * reminder carries its container as well, but it is a reminder about the
 * order. Everything else is a step of a container's own workflow.
 */
export function recordLink(row) {
  /**
   * A notice can name the screen it is about, when that is not the record's
   * own form. The CBM prices are typed on View Container - one box per
   * customer in it - so that is where "fill in the prices" has to land; the
   * container's form has nothing on it to fill.
   */
  if (row?.opens === "view_container" && row.container_id) {
    return `/view_container/${row.container_id}`;
  }

  if (row?.order_id) return `/orders?${PARAM}=${row.order_id}`;
  if (row?.container_id) return `/containers?${PARAM}=${row.container_id}`;

  // nothing to open - the full list is the honest place to land
  return "/notifications";
}

/**
 * Open the record the address names, once the screen is ready to.
 *
 * `ready` is for a screen that has to learn something before its form can
 * open correctly - the containers screen does not know which of its two
 * forms applies until its first load is back.
 *
 * The name is taken off the address as soon as it has done its work, without
 * adding to the history: a reload, or closing the form and pressing back,
 * must not open it again, and a second notice about the same record has to
 * be able to.
 */
export function useOpenFromUrl(openRecord, ready = true) {
  const [params, setParams] = useSearchParams();
  const wanted = params.get(PARAM);

  useEffect(() => {
    if (!wanted || !ready) return;

    // ids are numbers everywhere else on these screens
    openRecord(/^\d+$/.test(wanted) ? Number(wanted) : wanted);

    const rest = new URLSearchParams(params);
    rest.delete(PARAM);
    setParams(rest, { replace: true });
  }, [wanted, ready]);
}
